const BADGE_ID = 'userpilotContentNative';
const BADGE_SELECTOR = `[id="${BADGE_ID}"]`;
const HOTSPOT_SELECTOR = '.userpilot-hotspot, .userpilot-beacon-question, [id="beacon"]';
const HOOK_SELECTOR = '.userpilot-label-hook';
const FIELD_ROOT_SELECTOR = '.userpilot-field-root, .MuiFormControl-root, .form-group';
const MAX_BADGE_PX = 80;
const NEAREST_HOOK_PX = 96;

function readPx(el: HTMLElement, key: 'width' | 'height'): number {
  const offset = key === 'width' ? el.offsetWidth : el.offsetHeight;
  if (offset) return offset;
  const styled = parseFloat(el.style[key] || '');
  return Number.isFinite(styled) ? styled : 0;
}

function isHotspot(el: HTMLElement): boolean {
  return (
    el.classList.contains('userpilot-hotspot') ||
    el.classList.contains('userpilot-beacon-question') ||
    el.id === 'beacon'
  );
}

function isInlineBadge(el: HTMLElement): boolean {
  if (isHotspot(el)) return true;
  const width = readPx(el, 'width');
  const height = readPx(el, 'height');
  if (width === 0 && height === 0) return false;
  if (width > MAX_BADGE_PX || height > MAX_BADGE_PX) return false;
  return true;
}

function collectBadges(root: ParentNode): HTMLElement[] {
  const byId = Array.from(root.querySelectorAll<HTMLElement>(BADGE_SELECTOR));
  const byAttr = Array.from(root.querySelectorAll<HTMLElement>('[userpilotid]'));
  const hotspots = Array.from(root.querySelectorAll<HTMLElement>(HOTSPOT_SELECTOR));
  return [...new Set([...byId, ...byAttr, ...hotspots])].filter(isInlineBadge);
}

function hookHasHotspot(hook: HTMLElement): boolean {
  return Boolean(hook.querySelector(HOTSPOT_SELECTOR));
}

function hookHasBadge(hook: HTMLElement): boolean {
  return Boolean(hook.querySelector(`${BADGE_SELECTOR}, [userpilotid], ${HOTSPOT_SELECTOR}`));
}

function visualRect(el: HTMLElement): DOMRect {
  const self = el.getBoundingClientRect();
  if (self.width > 0 || self.height > 0) return self;
  const kids = el.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < kids.length; i += 1) {
    const rect = kids[i].getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return rect;
  }
  return self;
}

function hideNativeSpotlight(hook: HTMLElement): void {
  hook.querySelectorAll<HTMLElement>(BADGE_SELECTOR).forEach((el) => {
    if (!isHotspot(el)) el.style.setProperty('display', 'none', 'important');
  });
}

function hideStrayBadge(badge: HTMLElement): void {
  if (badge.closest(HOOK_SELECTOR)) return;
  badge.style.setProperty('display', 'none', 'important');
  badge.style.setProperty('visibility', 'hidden', 'important');
  badge.style.setProperty('opacity', '0', 'important');
  badge.style.setProperty('pointer-events', 'none', 'important');
}

export function getFieldRoot(hook: HTMLElement): HTMLElement | null {
  const scoped = hook.closest(FIELD_ROOT_SELECTOR) as HTMLElement | null;
  if (scoped) return scoped;

  let el: HTMLElement | null = hook.parentElement;
  while (el && el !== document.body) {
    if (el.classList.contains('gm-form-fields') || el.classList.contains('gm-form-grid')) {
      return hook.parentElement;
    }
    const hasControl = el.querySelector(
      'input, select, textarea, button[role="combobox"], .MuiSelect-root, .MuiRadio-root, .MuiCheckbox-root'
    );
    if (hasControl && el !== hook.parentElement) return el;
    el = el.parentElement;
  }
  return hook.parentElement;
}

function badgeOverlaps(field: HTMLElement, badge: HTMLElement): boolean {
  const fieldRect = field.getBoundingClientRect();
  const badgeRect = visualRect(badge);
  if (fieldRect.width === 0 && fieldRect.height === 0) return false;
  if (badgeRect.width === 0 && badgeRect.height === 0) return false;
  const cx = badgeRect.left + badgeRect.width / 2;
  const cy = badgeRect.top + badgeRect.height / 2;
  return cx >= fieldRect.left && cx <= fieldRect.right && cy >= fieldRect.top && cy <= fieldRect.bottom;
}

function fieldArea(field: HTMLElement): number {
  const rect = field.getBoundingClientRect();
  return Math.max(0, rect.width) * Math.max(0, rect.height);
}

function pinStyle(el: HTMLElement, props: Record<string, string>): void {
  Object.entries(props).forEach(([key, value]) => {
    if (el.style.getPropertyValue(key) === value && el.style.getPropertyPriority(key) === 'important') {
      return;
    }
    el.style.setProperty(key, value, 'important');
  });
}

function pinHotspotLayout(badge: HTMLElement): void {
  pinStyle(badge, {
    position: 'relative',
    top: '0px',
    left: '0px',
    right: 'auto',
    bottom: 'auto',
    width: '17px',
    height: '17px',
    'margin-top': '0px',
    'margin-right': '0px',
    'margin-bottom': '0px',
    'margin-left': '0px',
    transform: 'none',
    display: 'inline-flex',
    'align-items': 'center',
    'justify-content': 'center',
    overflow: 'visible',
    visibility: 'visible',
    opacity: '1',
    'pointer-events': 'auto',
    float: 'none',
  });
}

/**
 * Userpilot keeps writing inline offsets (margin-top/left, iframe top/left -8.5px)
 * that pull the "i" off the hook. Re-pin after every SDK style update.
 */
export function pinUserpilotBadgeLayout(badge: HTMLElement): void {
  if (isHotspot(badge)) {
    pinHotspotLayout(badge);
    return;
  }

  pinStyle(badge, {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '16px',
    height: '16px',
    'margin-top': '0px',
    'margin-right': '0px',
    'margin-bottom': '0px',
    'margin-left': '0px',
    transform: 'translate(-50%, -50%)',
    display: 'block',
  });

  const container = badge.querySelector<HTMLElement>('#userpilotContentContainer');
  if (container) {
    pinStyle(container, {
      position: 'absolute',
      top: '0px',
      left: '0px',
      'margin-top': '0px',
      'margin-right': '0px',
      'margin-bottom': '0px',
      'margin-left': '0px',
      transform: 'none',
      'transform-origin': 'center center',
      width: '16px',
      height: '16px',
    });
  }

  const iframe = badge.querySelector<HTMLElement>('#userpilotContentNativeIframe');
  if (iframe) {
    pinStyle(iframe, {
      position: 'absolute',
      top: '0px',
      left: '0px',
      width: '16px',
      height: '16px',
      transform: 'none',
    });
  }
}

function pinBadgesInHooks(root: ParentNode): void {
  collectBadges(root).forEach((badge) => {
    const hook = badge.closest(HOOK_SELECTOR) as HTMLElement | null;
    if (!hook) return;
    if (!isHotspot(badge) && hookHasHotspot(hook)) {
      badge.style.setProperty('display', 'none', 'important');
      return;
    }
    pinUserpilotBadgeLayout(badge);
  });
}

function adoptBadge(hook: HTMLElement, badge: HTMLElement): boolean {
  if (hook.contains(badge)) {
    if (isHotspot(badge)) hideNativeSpotlight(hook);
    return false;
  }

  // The visible "i" on radio fields is the hotspot. Prefer it over an empty
  // #userpilotContentNative spotlight that already sits in the hook.
  if (isHotspot(badge)) {
    hook.appendChild(badge);
    hideNativeSpotlight(hook);
    pinUserpilotBadgeLayout(badge);
    return true;
  }

  if (hookHasHotspot(hook)) {
    hideStrayBadge(badge);
    return false;
  }

  if (!hookHasBadge(hook)) {
    hook.appendChild(badge);
    pinUserpilotBadgeLayout(badge);
    return true;
  }
  hideStrayBadge(badge);
  return false;
}

export function findHookForBadge(badge: HTMLElement, hooks: HTMLElement[]): HTMLElement | null {
  const contained = hooks.filter((hook) => {
    const field = getFieldRoot(hook);
    return field != null && field.contains(badge);
  });
  if (contained.length === 1) return contained[0];
  if (contained.length > 1) {
    return contained.sort((a, b) => fieldArea(getFieldRoot(a)!) - fieldArea(getFieldRoot(b)!))[0];
  }

  const overlapping = hooks.filter((hook) => {
    const field = getFieldRoot(hook);
    return field != null && badgeOverlaps(field, badge);
  });
  if (overlapping.length === 1) return overlapping[0];
  if (overlapping.length > 1) {
    return overlapping.sort((a, b) => fieldArea(getFieldRoot(a)!) - fieldArea(getFieldRoot(b)!))[0];
  }

  const badgeRect = visualRect(badge);
  if (badgeRect.width === 0 && badgeRect.height === 0) return null;
  const cy = badgeRect.top + badgeRect.height / 2;
  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  hooks.forEach((hook) => {
    const field = getFieldRoot(hook);
    if (!field) return;
    const fieldRect = field.getBoundingClientRect();
    if (fieldRect.height === 0) return;
    const dist =
      cy < fieldRect.top ? fieldRect.top - cy : cy > fieldRect.bottom ? cy - fieldRect.bottom : 0;
    if (dist < bestDist) {
      bestDist = dist;
      best = hook;
    }
  });
  return bestDist <= NEAREST_HOOK_PX ? best : null;
}

/** Move Userpilot "i" badges that landed on options / mid-label onto the field hook. */
export function relocateUserpilotBadges(root: ParentNode = document): number {
  const hooks = Array.from(root.querySelectorAll<HTMLElement>(HOOK_SELECTOR));
  let moved = 0;

  hooks.forEach((hook) => {
    const field = getFieldRoot(hook);
    if (!field) return;

    collectBadges(field)
      .filter((badge) => !hook.contains(badge))
      .sort((a, b) => Number(isHotspot(b)) - Number(isHotspot(a)))
      .forEach((badge) => {
        if (adoptBadge(hook, badge)) moved += 1;
      });
  });

  const searchRoot = root instanceof Document ? root : document;
  collectBadges(searchRoot)
    .filter((badge) => !badge.closest(HOOK_SELECTOR))
    .sort((a, b) => Number(isHotspot(b)) - Number(isHotspot(a)))
    .forEach((badge) => {
      const hook = findHookForBadge(badge, hooks);
      if (!hook) {
        hideStrayBadge(badge);
        return;
      }
      if (adoptBadge(hook, badge)) moved += 1;
    });

  pinBadgesInHooks(searchRoot);
  collectBadges(searchRoot).forEach(hideStrayBadge);
  return moved;
}

function isUserpilotStyleTarget(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const id = node.id;
  return (
    id === BADGE_ID ||
    id === 'userpilotContentContainer' ||
    id === 'userpilotContentNativeIframe' ||
    id === 'beacon' ||
    node.hasAttribute('userpilotid') ||
    node.classList.contains('userpilot-hotspot') ||
    node.classList.contains('userpilot-beacon-question') ||
    Boolean(node.closest(`${BADGE_SELECTOR}, ${HOTSPOT_SELECTOR}`))
  );
}

let stopRelocator: (() => void) | null = null;

export function startUserpilotBadgeRelocator(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  if (stopRelocator) return stopRelocator;

  let scheduled = false;
  const run = () => {
    scheduled = false;
    relocateUserpilotBadges();
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(run);
  };

  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      if (mutation.type === 'childList') return true;
      return isUserpilotStyleTarget(mutation.target);
    });
    if (relevant) schedule();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });
  const intervalId = window.setInterval(run, 100);
  schedule();

  stopRelocator = () => {
    observer.disconnect();
    window.clearInterval(intervalId);
    stopRelocator = null;
  };
  return stopRelocator;
}
